/**
 * Pure helpers for FormField: display label, visibility, editability, select display, async response normalizer.
 * Extracted for unit testing and reuse.
 */
import { get } from "lodash";
import Cookies from "js-cookie";
import { loadPersistedDomain } from "../../utils/domainPersistence";
import type {
  FormFieldConfig,
  FormData,
  OptionItem,
  ParamFromState,
} from "./types";

export const getDisplayLabel = (config: FormFieldConfig): string =>
  config.label ?? config.originlabel;

/**
 * Whether the field should be shown based on dependencies.show rules (eq / in).
 */
export function shouldShowField(config: FormFieldConfig, formData: FormData): boolean {
  if (!config.dependencies?.show?.length) return true;
  for (const rule of config.dependencies.show) {
    const watchValue = formData[rule.when];
    if (rule.eq !== undefined && watchValue !== rule.eq) return false;
    if (rule.in !== undefined && !rule.in.includes(watchValue)) return false;
  }
  return true;
}

/**
 * Effective editability from config: only configurability.
 * When configurability is false or "false", field is not editable (grey).
 * When configurability is true, "true", or undefined, field is editable (black border).
 */
export function getEffectiveEditability(config: FormFieldConfig): {
  readOnly: boolean;
  disabled: boolean;
} {
  const cfg = config.configurability;
  const notEditable =
    cfg === false || cfg === "false" || cfg === 0;
  return {
    readOnly: notEditable,
    disabled: notEditable,
  };
}

export const TASK_OWNER_GROUP_LABELS: Record<string, string> = {
  taskUsers: "Users",
  taskGroups: "User Groups",
  taskRoles: "Roles",
};

/**
 * Format grouped task-owners value for display when options are not available.
 */
export function formatGroupedTaskOwners(val: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, label] of Object.entries(TASK_OWNER_GROUP_LABELS)) {
    const arr = val[key];
    if (Array.isArray(arr) && arr.length > 0) {
      parts.push(`${arr.length} ${label}`);
    }
  }
  return parts.length > 0 ? parts.join(", ") : "";
}

/**
 * Get display string for a select value; handles primitives, option lookup, and objects (e.g. Task Owners).
 */
export function selectValueToDisplay(
  val: unknown,
  options?: OptionItem[]
): string {
  if (val === null || val === undefined) return "";
  if (typeof val === "string" || typeof val === "number") {
    const found = options?.find((o) => o.value === val);
    return found ? found.label : String(val);
  }
  if (typeof val === "object" && val !== null && !Array.isArray(val)) {
    const o = val as Record<string, unknown>;
    const name = o.name ?? o.label ?? o.displayName ?? o.title;
    if (typeof name === "string") return name;
    if (typeof name === "number") return String(name);
    const grouped = formatGroupedTaskOwners(o);
    if (grouped) return grouped;
    return "";
  }
  return "";
}

/**
 * Whether asyncConfig URL is for task-owners or task-assignees.
 */
export function isTaskOwnerOrAssigneeUrl(url: string): boolean {
  return /task-owners|task-assignees/i.test(url);
}

/**
 * Whether asyncConfig URL is for task-owners (GET, nested data structure).
 */
export function isTaskOwnersUrl(url: string): boolean {
  return /task-owners/i.test(url);
}

/**
 * Whether asyncConfig URL is for task-assignees (POST with body).
 */
export function isTaskAssigneesUrl(url: string): boolean {
  return /task-assignees/i.test(url);
}

/**
 * Build POST body from asyncConfig.body using formData.
 * Each body item: value from formData[valuePath] or formData when valuePath is "all".
 */
export function buildBodyFromFormState(
  bodyConfig: ParamFromState[] | undefined,
  formData: FormData
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!Array.isArray(bodyConfig) || bodyConfig.length === 0) return result;
  const store = formData;
  for (const el of bodyConfig) {
    let val: unknown;
    if (el.value !== undefined) {
      val = el.value;
    } else if (el.valuePath === "all") {
      val = store;
    } else {
      val = get(store, el.valuePath ?? el.fieldName, undefined);
    }
    result[el.fieldName] = val;
  }
  return result;
}

/**
 * Get values of dependency fields for refetch trigger.
 */
export function getDependencyValues(
  dependencies: string[] | undefined,
  formData: FormData
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!Array.isArray(dependencies) || dependencies.length === 0) return result;
  for (const key of dependencies) {
    result[key] = get(formData, key, undefined);
  }
  return result;
}

/**
 * Get tenantId and domainName from cookies for task-assignees POST request.
 * tenantId: USER_TENANT_ID or TENANT_ID
 * domainName: TASK_DOMAIN_NAME (for task-assignees API, e.g. "1") or DOMAIN (product type fallback)
 */
export function getTaskAssigneesRequestCookies(): {
  tenantId: string;
  domainName: string;
} {
  const tenantId =
    Cookies.get("USER_TENANT_ID") ?? Cookies.get("TENANT_ID") ?? "";
  const domainName =
    Cookies.get("TASK_DOMAIN_NAME") ?? loadPersistedDomain() ?? "";
  return { tenantId, domainName };
}

/**
 * Normalize taskOwners for task-assignees POST body.
 * Form may store taskOwners as flat array ["id1","id2"]; API expects { taskUsers, taskGroups, taskRoles }.
 * Uses cached id->type map from last task-owners API response.
 */
export function normalizeTaskOwnersForRequest(value: unknown): {
  taskUsers: string[];
  taskGroups: string[];
  taskRoles: string[];
} {
  if (value == null)
    return {
      taskUsers: [],
      taskGroups: [],
      taskRoles: [],
    };

  // Already in correct shape (plain object with taskUsers/taskGroups/taskRoles)
  if (typeof value === "object" && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const hasGroupedKeys =
      o.taskUsers !== undefined ||
      o.taskGroups !== undefined ||
      o.taskRoles !== undefined;
    if (hasGroupedKeys) {
      const coerceBucket = (val: unknown): string[] => {
        if (Array.isArray(val)) {
          return (val as unknown[]).map(String).filter(Boolean);
        }
        if (val != null && val !== "") {
          return [String(val)];
        }
        return [];
      };
      return {
        taskUsers: coerceBucket(o.taskUsers),
        taskGroups: coerceBucket(o.taskGroups),
        taskRoles: coerceBucket(o.taskRoles),
      };
    }
  }

  // Flat array: map each ID to type using cache from task-owners response
  if (Array.isArray(value)) {
    const result = {
      taskUsers: [] as string[],
      taskGroups: [] as string[],
      taskRoles: [] as string[],
    };
    for (const v of value) {
      const id = v != null && v !== "" ? String(v) : "";
      if (!id) continue;
      const type = taskOwnersIdToTypeMap.get(id) ?? "taskUsers";
      if (type === "taskUsers") result.taskUsers.push(id);
      else if (type === "taskGroups") result.taskGroups.push(id);
      else if (type === "taskRoles") result.taskRoles.push(id);
    }
    return result;
  }

  return {
    taskUsers: [],
    taskGroups: [],
    taskRoles: [],
  };
}

/** Whether config type supports options (select, radio, checkboxGroups). */
export function isOptionsFieldType(type: string): boolean {
  return type === "select" || type === "radio" || type === "checkboxGroups";
}

/** Get base options: async when loaded, else static, else async fallback. */
export function getBaseOptionsForField(
  useAsyncAsBase: boolean,
  staticOptions: OptionItem[] | undefined,
  asyncOptions: OptionItem[]
): OptionItem[] {
  if (useAsyncAsBase) return [...asyncOptions];
  if (staticOptions?.length) return staticOptions;
  return [...asyncOptions];
}

/** Get values that must appear in options (current selection). */
export function getValuesToCover(
  multiple: boolean | undefined,
  value: unknown
): unknown[] {
  if (multiple && isTaskOwnerStructure(value)) {
    return extractIdsFromTaskOwnerStructure(value);
  }
  if (multiple && Array.isArray(value)) return value;
  if (value != null && value !== "") return [value];
  return [];
}

/** Check if value exists in options. */
export function isValueInOptions(base: OptionItem[], v: unknown): boolean {
  return base.some((o) => o.value === v || String(o.value) === String(v));
}

/** Get display label for a value not in options (e.g. object for task owners). */
export function getLabelForValueNotInOptions(v: unknown): string {
  if (typeof v === "object" && v !== null && !Array.isArray(v)) {
    return formatGroupedTaskOwners(v as Record<string, unknown>) || "—";
  }
  return String(v);
}

/** Ensure all valuesToCover are in base; prepend missing ones with placeholder labels. */
export function ensureValuesInOptions(
  base: OptionItem[],
  valuesToCover: unknown[]
): OptionItem[] {
  let result = base;
  for (const v of valuesToCover) {
    if (!isValueInOptions(result, v)) {
      const label = getLabelForValueNotInOptions(v);
      result = [{ label, value: v as string | number }, ...result];
    }
  }
  return result;
}

const TASK_OWNER_GROUP_KEYS = ["taskUsers", "taskGroups", "taskRoles"] as const;

/** Cache: id -> "taskUsers" | "taskGroups" | "taskRoles" for mapping flat IDs back to structure */
let taskOwnersIdToTypeMap: Map<string, string> = new Map();

/** Check if value is task-owners structure { taskUsers?, taskGroups?, taskRoles? } */
export function isTaskOwnerStructure(value: unknown): value is Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return false;
  const o = value as Record<string, unknown>;
  return TASK_OWNER_GROUP_KEYS.some((k) => Array.isArray(o[k]));
}

/** Extract IDs from task-owners structure for Select value and option lookup. */
export function extractIdsFromTaskOwnerStructure(value: unknown): string[] {
  if (!isTaskOwnerStructure(value)) return [];
  const result: string[] = [];
  for (const key of TASK_OWNER_GROUP_KEYS) {
    const arr = (value as Record<string, unknown>)[key];
    if (Array.isArray(arr)) {
      for (const id of arr) {
        if (id != null && id !== "") result.push(String(id));
      }
    }
  }
  return result;
}

/** Build task-owners structure from IDs using cache from task-owners API response. */
export function buildTaskOwnerStructureFromIds(ids: string[]): Record<string, string[]> {
  const result: Record<string, string[]> = {
    taskUsers: [],
    taskGroups: [],
    taskRoles: [],
  };
  for (const id of ids) {
    if (!id) continue;
    const type = taskOwnersIdToTypeMap.get(id) ?? "taskUsers";
    if (result[type]) result[type].push(id);
  }
  return result;
}

/**
 * Flatten task-owners API response: { data: { taskUsers: [...], taskGroups: [...], taskRoles: [...] } }
 * into a single array of items. Also populates id->type map for task-assignees request.
 */
function flattenTaskOwnersData(data: unknown): unknown[] {
  if (data == null || typeof data !== "object") return [];
  const obj = data as Record<string, unknown>;
  const result: unknown[] = [];
  const idMap = new Map<string, string>();
  for (const key of TASK_OWNER_GROUP_KEYS) {
    const arr = obj[key];
    if (Array.isArray(arr)) {
      for (const item of arr) {
        result.push(item);
        const o = item as Record<string, unknown>;
        const id = String(o?.id ?? o?.value ?? o?.code ?? "");
        if (id) idMap.set(id, key);
      }
    }
  }
  taskOwnersIdToTypeMap = idMap;
  return result;
}

/**
 * Extract flattened list from task-owners API response for Dynamic Form.
 * Response: { data?: { taskUsers?, taskGroups?, taskRoles? } } or axios wrapped.
 */
export function extractTaskOwnersListFromResponse(body: unknown): unknown[] {
  const data = body && typeof body === "object"
    ? (body as Record<string, unknown>).data
    : null;
  return flattenTaskOwnersData(data);
}

/**
 * Normalize task-owners API response to OptionItem[].
 * Response: { code, message, data: { taskUsers: [...], taskGroups: [...], taskRoles: [...] } }
 */
export function normalizeTaskOwnersResponseToOptions(
  body: unknown,
  options: { labelPath?: string; valuePath?: string }
): OptionItem[] {
  const labelPath = options.labelPath ?? "name";
  const valuePath = options.valuePath ?? "id";
  const data = body && typeof body === "object"
    ? (body as Record<string, unknown>).data
    : null;
  const list = flattenTaskOwnersData(data);
  return list.map((item: unknown) => {
    const o = item as Record<string, unknown>;
    return {
      label: String(o[labelPath] ?? o.name ?? o.label ?? ""),
      value: (o[valuePath] ?? o.id ?? o.code ?? o.value ?? "") as string | number,
    };
  });
}

/**
 * Normalize task-assignees API response to OptionItem[].
 * Response: { code, message, data: [...] }
 */
export function normalizeTaskAssigneesResponseToOptions(
  body: unknown,
  options: { labelPath?: string; valuePath?: string }
): OptionItem[] {
  const labelPath = options.labelPath ?? "name";
  const valuePath = options.valuePath ?? "id";
  const data = body && typeof body === "object"
    ? (body as Record<string, unknown>).data
    : null;
  const list = Array.isArray(data) ? data : [];
  return list.map((item: unknown) => {
    const o = item as Record<string, unknown>;
    return {
      label: String(o[labelPath] ?? o.name ?? o.label ?? ""),
      value: (o[valuePath] ?? o.id ?? o.code ?? o.value ?? "") as string | number,
    };
  });
}

/**
 * Normalize API response body to OptionItem[].
 * Handles: raw array, listDataPath, and KM "concepts/values" shape (flatten .values).
 */
export function normalizeAsyncResponseToOptions(
  body: unknown,
  options: {
    labelPath?: string;
    valuePath?: string;
    listDataPath?: string;
  }
): OptionItem[] {
  const labelPath = options.labelPath ?? "name";
  const valuePath = options.valuePath ?? "code";
  const { listDataPath } = options;

  const list = listDataPath && body && typeof body === "object"
    ? (body as Record<string, unknown>)[listDataPath]
    : body;
  const arr = Array.isArray(list) ? list : [];

  const flattened = arr.some(
    (x: unknown) =>
      x &&
      typeof x === "object" &&
      Array.isArray((x as Record<string, unknown>).values)
  )
    ? arr.flatMap((x: unknown) =>
        Array.isArray((x as Record<string, unknown>)?.values)
          ? (x as Record<string, unknown[]>).values
          : []
      )
    : arr;

  return flattened.map((item: Record<string, unknown>) => ({
    label: String(
      item[labelPath] ?? item.name ?? item.label ?? ""
    ),
    value: (item[valuePath] ?? item.code ?? item.value ?? "") as string | number,
  }));
}
